// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  // Table,
  // TableContainer,
  // TableRow,
  // TableCell,
  // TableBody,
  // styled,
  Typography,
  Alert as MuiAlert,
  Link as MuiLink,
  Tooltip
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'

// component import
import { Alert } from '../../common/Alert'
import { CustomController } from '../../common/CustomController'
import AccountDropdown from '../../common/AccountDropdown'
import PaymentMethodController from '../../common/PaymentMethodController'
// import CheckboxController from '../../common/CheckboxController'
// import InfoTooltip from '../../common/InfoTooltip'
import OfficeAutoComplete from '../../offices/OfficeAutocomplete'
import { CustomDatePickerController } from '../../common/CustomDatePickerRController'
import DepositMatterDropdown from './DepositMatterDropdown'
import DepositClientDropdown, { type ClientOption } from './DepositClientDropdown'
import BillPreviewPanel from './BillPreviewPanel'
import VendorAutoComplete from '../../vendors/VendorAutoComplete'

// others
import {
  AccountType,
  BillingType,
  BillStatus,
  Case,
  Maybe,
  PaymentMethod,
  useApplyCreditsMutation,
  useCreateFirmPaymentMutation,
  useFetchOperatingAccountLazyQuery,
  useGetClientCreditBalanceLazyQuery,
  // useFetchRemainingFlatFeeLazyQuery,
  useFetchRetainerAccountLazyQuery,
  useFetchZescrowAccountLazyQuery,
  // useGetCaseBillingPauseDateLazyQuery,
  useRecordClientCreditsMutation,
  useRecordManualPaymentMutation,
  useRefundPaymentMutation
} from '../../../../generated'
import {
  BAD_USER_INPUT,
  GRAPHQL_QUERY_POLICY,
  PAYMENTS_ROUTE,
  PRE_BILL_STATUSES,
  QUERY_PARAMS,
  SOMETHING_WENT_WRONG
} from '../../../constants'
import { RecordPaymentFormValues } from '../../../interfaceTypes/forms'
import { recordPaymentValidationSchema } from '../../../validationSchema'
import { formatCurrency, formatToTwoDecimals, hasAdminAccess } from '../../../utils'
import { AuthContext } from '../../../context'
import { useBillActions } from '../../../hooks/useBillActions'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import dayjs from 'dayjs'
import useFetchBillDetailsByMatter from '../../../hooks/useFetchBillDetailsByMatter'
import { DepositType, DEPOSIT_TYPE_LABELS } from './constants'
import FirmPaymentSuccessMessage from './FirmPaymentSuccessMessage'
import GenerateMonthlyFlatFeeBillModal from '../../billing/monthlyFlatFee/GenerateMonthlyFlatFeeBillModal'
import QuickBillModal from '../../billing/quickBill/QuickBillModal'

const availablePaymentMethods = [
  PaymentMethod.Cash,
  PaymentMethod.Cheque,
  PaymentMethod.Card,
  PaymentMethod.Ach,
  PaymentMethod.Eft,
  PaymentMethod.WireTransfer,
  PaymentMethod.Other
]

const getFormDefaultValues = (): RecordPaymentFormValues => ({
  amount: '',
  isAdvancePayment: false,
  date: new Date().toISOString().split('T')[0],
  paymentMethod: PaymentMethod.Cheque,
  referenceNo: '',
  slipNumber: '',
  description: '',
  destinationAccount: undefined as unknown as RecordPaymentFormValues['destinationAccount'],
  pauseBillingUntil: null,
  location: { value: 0, label: '' },
  client: null,
  vendor: null,
  glAccount: null
})

export interface LastRecordedTransactionSnapshot {
  matterTitle: string
  matterNumber: string
  matterId: string
  clientName: string
  clientNumber: string
  depositType: DepositType
  destinationAccountLabel: string
  paymentMethod: string
  amount: number
}

// const CustomTable = styled(Table)(({ theme }) => ({
//   border: '1px solid #E0E0E0',
//   '& .MuiTableCell-root': {
//     padding: theme.spacing(1.5),
//     fontSize: '14px !important'
//   },
//   '& .MuiTableRow-root > .MuiTableCell-root:first-of-type': {
//     borderRight: '1px solid #E0E0E0',
//     fontWeight: '500'
//   }
// }))

const DepositFundForm: FC = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize from query params
  const [depositType, setDepositType] = useState<DepositType>(
    (searchParams.get('depositType') as DepositType) || DepositType.CLIENT_CREDIT
  )
  const [selectedMatter, setSelectedMatter] = useState<Maybe<Case>>(null)
  const [selectedClient, setSelectedClient] = useState<Maybe<ClientOption>>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  // const [remainingFlatFee, setRemainingFlatFee] = useState(0)
  const [pendingFormData, setPendingFormData] = useState<RecordPaymentFormValues | null>(null)
  const [lastRecordedTransaction, setLastRecordedTransaction] = useState<LastRecordedTransactionSnapshot | null>(null)
  const [officeResetKey, setOfficeResetKey] = useState(new Date().getTime())
  const [useCredits, setUseCredits] = useState(false)
  const [generateClientBillCopy, setGenerateClientBillCopy] = useState(false)
  const [clientCreditBalance, setClientCreditBalance] = useState(0)
  const [isAddNew, setIsAddNew] = useState(false)
  const isAddNewRef = useRef(false)
  const [showResetConfirmation, setShowResetConfirmation] = useState(false)
  const [showMonthlyFlatFeeModal, setShowMonthlyFlatFeeModal] = useState(false)
  const [showQuickBillModal, setShowQuickBillModal] = useState(false)

  const matterId = selectedMatter?.id || undefined
  const isClientPayment = depositType === DepositType.CLIENT
  const isFirmPayment = depositType === DepositType.FIRM
  const isClientCredit = depositType === DepositType.CLIENT_CREDIT
  const isRefundPayment = depositType === DepositType.REFUND
  const clientId = selectedClient?.id || searchParams.get('clientId') || undefined

  // The "generate client bill copy" opt-in only applies to client payments; clear it otherwise.
  useEffect(() => {
    if (!isClientPayment && generateClientBillCopy) setGenerateClientBillCopy(false)
  }, [isClientPayment, generateClientBillCopy])

  const { currentUser } = useContext(AuthContext)

  // Fetch bill details for client payment
  const {
    bill,
    clientData: billClientData,
    interestData,
    outstandingBillsData,
    refetch: refetchBill,
    loading: billLoading
  } = useFetchBillDetailsByMatter({
    matterId: isClientPayment ? matterId : undefined,
    clientId,
    includeInterest: true
  })

  const isPreBill = bill && PRE_BILL_STATUSES.includes(bill.status)
  const canAdminApprovePreBill = isPreBill && hasAdminAccess(currentUser)

  const {
    departmentHead,
    showAdminApprovalConfirmation,
    setShowAdminApprovalConfirmation,
    approvingByAttorney,
    approvingByDepartmentHead,
    handleApproveByAttorney,
    handleApproveByDepartmentHead,
    fetchDepartmentHead,
    checkIsUserDepartmentHeadForCase
  } = useBillActions({
    onRefetch: refetchBill
  })

  const billApprover = bill?.case?.billing?.billApprover

  const handleAdminApproval = () => {
    if (!bill) return

    if ([BillStatus.Draft, BillStatus.SentForApproval].includes(bill.status)) {
      handleApproveByAttorney(bill.id)
    }

    if (bill.status === BillStatus.ApprovedByResponsibleAttorney) {
      handleApproveByDepartmentHead(bill.id)
    }
  }

  const getAdminApprovalConfirmationDialogDescription = () => {
    if ([BillStatus.Draft, BillStatus.SentForApproval].includes(bill!.status)) {
      return `Are you sure you want to approve this bill on behalf of Attorney ${billApprover?.firstName || ''} ${
        billApprover?.lastName || ''
      }? This action cannot be undone.`
    }

    return `Are you sure you want to approve this bill on behalf of Department Head ${
      departmentHead?.firstName || ''
    } ${departmentHead?.lastName || ''}? This action cannot be undone.`
  }

  useEffect(() => {
    if (bill?.status === BillStatus.ApprovedByResponsibleAttorney && bill?.case?.id) {
      fetchDepartmentHead(bill.case.id)
    }
  }, [bill?.status, bill?.case?.id, fetchDepartmentHead])

  useEffect(() => {
    if (bill?.status === BillStatus.ApprovedByResponsibleAttorney && bill?.case?.id && currentUser) {
      checkIsUserDepartmentHeadForCase(bill.case.id, currentUser.id)
    }
  }, [bill?.status, bill?.case?.id, currentUser, checkIsUserDepartmentHeadForCase])

  const clientPayableAmount = isClientPayment ? billClientData?.payableAmount || 0 : undefined
  // const billingType = bill?.case?.billing?.billingType
  // const isFlatFee = billingType === BillingType.FlatFee && matterId

  const methods = useForm<RecordPaymentFormValues>({
    mode: 'all',
    resolver: yupResolver(recordPaymentValidationSchema),
    defaultValues: getFormDefaultValues()
  })

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty }
  } = methods

  // const isAdvancePayment = watch('isAdvancePayment')
  const paymentAmount = Number(watch('amount')) || 0
  // const contractAfterAmount = Math.max(remainingFlatFee - paymentAmount, 0)
  const amountToBeAdjusted = useMemo(
    () => Math.max(formatToTwoDecimals(paymentAmount - (clientPayableAmount || 0)), 0),
    [clientPayableAmount, paymentAmount]
  )
  const hasExtraPayment = isClientPayment && !useCredits && amountToBeAdjusted > 0
  const destinationAccountValue = watch('destinationAccount')

  // Client payments and credits are firm money and must land in an operating bank; trust and escrow
  // deposits are client money and must land in a trust bank. Firm and refund payments keep every bank.
  const destinationAccountFilters = useMemo(() => {
    switch (depositType) {
      case DepositType.CLIENT:
      case DepositType.CLIENT_CREDIT:
        return { isBankAccount: true, type: AccountType.Operating }
      case DepositType.TRUST:
      case DepositType.ESCROW:
        return { isBankAccount: true, type: AccountType.Trust }
      default:
        return { isBankAccount: true }
    }
  }, [depositType])

  // Lazy queries for accounts
  const [fetchRetainerAccount] = useFetchRetainerAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchRetainerAccount) {
        const acc = data.fetchRetainerAccount
        setValue('destinationAccount', {
          value: acc.id,
          label: `(#${acc.sortCode}) ${acc.name || ''}`
        })
      }
    }
  })

  const [fetchZescrowAccount] = useFetchZescrowAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchZescrowAccount) {
        const acc = data.fetchZescrowAccount
        setValue('destinationAccount', {
          value: acc.id,
          label: `(#${acc.sortCode}) ${acc.name || ''}`
        })
      }
    }
  })

  const [fetchOperatingAccount] = useFetchOperatingAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchOperatingAccount) {
        const acc = data.fetchOperatingAccount
        setValue('destinationAccount', {
          value: acc.id,
          label: `(#${acc.sortCode}) ${acc.name || ''}`
        })
      }
    }
  })

  // const [fetchRemainingFlatFee] = useFetchRemainingFlatFeeLazyQuery({
  //   ...GRAPHQL_QUERY_POLICY,
  //   variables: undefined,
  //   onCompleted: data => {
  //     if (data.fetchRemainingFlatFee.remainingFlatFee) {
  //       setRemainingFlatFee(data.fetchRemainingFlatFee.remainingFlatFee)
  //     }
  //   }
  // })

  // const [getCaseBillingPauseDate] = useGetCaseBillingPauseDateLazyQuery({
  //   ...GRAPHQL_QUERY_POLICY,
  //   variables: undefined,
  //   onCompleted: data => {
  //     if (data.getCaseBillingPauseDate?.date) {
  //       setValue('pauseBillingUntil', new Date(data.getCaseBillingPauseDate.date).toISOString().split('T')[0])
  //     }
  //   }
  // })

  const [getClientCreditBalance] = useGetClientCreditBalanceLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted(data) {
      const balance = data?.getClientCreditBalance?.creditBalance ?? 0
      setClientCreditBalance(balance)
      if (balance <= 0) setUseCredits(false)
    }
  })

  const [applyCreditsMutation, { loading: applyCreditsLoading }] = useApplyCreditsMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors?.length) {
        graphQLErrors.forEach(({ extensions }) => {
          if (extensions?.code === BAD_USER_INPUT) Alert.error(error.message)
          else Alert.error(SOMETHING_WENT_WRONG)
        })
      } else Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      if (data?.applyCredits?.response?.status === 200) {
        Alert.success('Credits applied successfully')
        setShowConfirmation(false)
        setLastRecordedTransaction({
          matterTitle: matterTitle || '',
          matterNumber: matterNumber || '',
          matterId: matterId || '',
          clientName: selectedClient?.label || '',
          clientNumber: selectedClient?.clientNumber || '',
          depositType: DepositType.CLIENT,
          destinationAccountLabel: '',
          paymentMethod: 'Apply Credits',
          amount: Number(pendingFormData?.amount) || 0
        })
        setPendingFormData(null)
        if (isAddNewRef.current) partialReset()
        else resetForm()
      }
    }
  })

  const [createFirmPaymentMutation, { loading: createFirmPaymentLoading }] = useCreateFirmPaymentMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors?.length) {
        graphQLErrors.forEach(({ extensions }) => {
          if (extensions?.code === BAD_USER_INPUT) Alert.error(error.message)
          else Alert.error(SOMETHING_WENT_WRONG)
        })
      } else Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      if (data?.createFirmPayment?.response?.status === 200) {
        Alert.success('Firm payment recorded successfully')
        setShowConfirmation(false)
        setLastRecordedTransaction({
          matterTitle: '-',
          matterNumber: '-',
          matterId: '-',
          clientName: '-',
          clientNumber: '-',
          depositType: DepositType.FIRM,
          destinationAccountLabel: destinationAccountValue?.label || '',
          paymentMethod: pendingFormData?.paymentMethod || '',
          amount: Number(pendingFormData?.amount) || 0
        })
        setPendingFormData(null)
        if (isAddNewRef.current) partialReset()
        else resetForm()
      }
    }
  })
  const [recordClientCreditsMutation, { loading: recordClientCreditsLoading }] = useRecordClientCreditsMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors?.length) {
        graphQLErrors.forEach(({ extensions }) => {
          if (extensions?.code === BAD_USER_INPUT) Alert.error(error.message)
          else Alert.error(SOMETHING_WENT_WRONG)
        })
      } else Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      if (data?.recordClientCredits?.response?.status === 200) {
        Alert.success('Client credits recorded successfully')
        setShowConfirmation(false)
        setLastRecordedTransaction({
          matterTitle: matterTitle || '',
          matterNumber: matterNumber || '',
          matterId: matterId || '',
          clientName: selectedClient?.label || '',
          clientNumber: selectedClient?.clientNumber || '',
          depositType,
          destinationAccountLabel: destinationAccountValue?.label || '',
          paymentMethod: pendingFormData?.paymentMethod || '',
          amount: Number(pendingFormData?.amount) || 0
        })
        setPendingFormData(null)
        if (isAddNewRef.current) partialReset()
        else resetForm()
      }
    }
  })

  const [recordPaymentMutation, { loading: recordPaymentLoading }] = useRecordManualPaymentMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors) {
        graphQLErrors.map(({ extensions }) => {
          if (extensions) {
            const { code } = extensions
            if (code === BAD_USER_INPUT) {
              Alert.error(error.message)
            } else {
              Alert.error(SOMETHING_WENT_WRONG)
            }
          }
          return null
        })
      } else {
        Alert.error(SOMETHING_WENT_WRONG)
      }
    },
    onCompleted(data) {
      if (data) {
        const {
          recordManualPayment: { response }
        } = data
        if (response) {
          const { status } = response
          if (status === 200) {
            Alert.success('Payment recorded successfully')
            setShowConfirmation(false)
            setLastRecordedTransaction({
              matterTitle: matterTitle || '',
              matterNumber: matterNumber || '',
              matterId: matterId || '',
              clientName: selectedClient?.label || '',
              clientNumber: selectedClient?.clientNumber || '',
              depositType,
              destinationAccountLabel: destinationAccountValue?.label || '',
              paymentMethod: pendingFormData?.paymentMethod || '',
              amount: Number(pendingFormData?.amount) || 0
            })
            setPendingFormData(null)
            if (isAddNewRef.current) partialReset()
            else resetForm()
          }
        }
      }
    }
  })

  const [refundPaymentMutation, { loading: refundPaymentLoading }] = useRefundPaymentMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors?.length) {
        graphQLErrors.forEach(({ extensions }) => {
          if (extensions?.code === BAD_USER_INPUT) Alert.error(error.message)
          else Alert.error(SOMETHING_WENT_WRONG)
        })
      } else Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      if (data?.refundPayment?.response?.status === 200) {
        Alert.success('Refund recorded successfully')
        setShowConfirmation(false)
        setLastRecordedTransaction({
          matterTitle: matterTitle || '',
          matterNumber: matterNumber || '',
          matterId: matterId || '',
          clientName: selectedClient?.label || '',
          clientNumber: selectedClient?.clientNumber || '',
          depositType: DepositType.REFUND,
          destinationAccountLabel: destinationAccountValue?.label || '',
          paymentMethod: pendingFormData?.paymentMethod || '',
          amount: Number(pendingFormData?.amount) || 0
        })
        setPendingFormData(null)
        if (isAddNewRef.current) partialReset()
        else resetForm()
      }
    }
  })

  const toLocalDateTime = (dateString: string): Date => {
    const now = dayjs()
    return dayjs(dateString)
      .hour(now.hour())
      .minute(now.minute())
      .second(now.second())
      .millisecond(now.millisecond())
      .toDate()
  }

  // Pre-fill destination account based on deposit type
  useEffect(() => {
    switch (depositType) {
      case DepositType.TRUST:
        fetchRetainerAccount()
        break
      case DepositType.ESCROW:
        fetchZescrowAccount()
        break
      case DepositType.CLIENT:
      case DepositType.CLIENT_CREDIT:
      case DepositType.FIRM:
      case DepositType.REFUND:
        fetchOperatingAccount()
        break
    }
  }, [depositType, fetchRetainerAccount, fetchZescrowAccount, fetchOperatingAccount])

  // // Fetch remaining flat fee for client payment flat fee cases
  // useEffect(() => {
  //   if (isClientPayment && billingType === BillingType.FlatFee && matterId) {
  //     fetchRemainingFlatFee({
  //       variables: { fetchRemainingFlatFeeInput: { caseId: matterId } }
  //     })
  //   }
  // }, [isClientPayment, billingType, matterId, fetchRemainingFlatFee])

  // Reset the amount to 0 for client payments instead of pre-filling the bill's due amount.
  // Clients usually pay a partial amount, so pre-filling the full due forced them to clear the
  // field every time. The due amount is now surfaced as a "Due Amt" hint under the field.
  useEffect(() => {
    if (isClientPayment && clientPayableAmount !== undefined) {
      setValue('amount', 0)
    }
  }, [isClientPayment, clientPayableAmount, setValue])

  // // Billing pause date for advance payments
  // useEffect(() => {
  //   if (isAdvancePayment && matterId && remainingFlatFee) {
  //     getCaseBillingPauseDate({
  //       variables: { advancePayment: amountToBeAdjusted, caseId: matterId }
  //     })
  //   }
  // }, [isAdvancePayment, matterId, getCaseBillingPauseDate, remainingFlatFee, amountToBeAdjusted])

  // Fetch client credit balance when on Client Payment with bill
  useEffect(() => {
    if (isClientPayment && matterId && clientId && bill) {
      getClientCreditBalance({
        variables: { getClientCreditBalanceInput: { caseId: matterId, clientId } }
      })
    }
    // Use bill?.id and bill?.status instead of bill object to avoid re-firing on every refetch
    // (bill object gets a new reference even when the same bill is refetched)
    // but still react to status changes (e.g. pre-bill approval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientPayment, matterId, clientId, bill?.id, bill?.status, getClientCreditBalance])

  // Update query params when selections change
  const updateQueryParams = useCallback(
    (newDepositType?: DepositType, newMatterId?: string, newClientId?: string) => {
      const params = new URLSearchParams()
      const dt = newDepositType || depositType
      if (dt) params.set('depositType', dt)
      const mid = newMatterId !== undefined ? newMatterId : matterId || ''
      if (mid) params.set('matterId', mid)
      const cid = newClientId !== undefined ? newClientId : clientId || ''
      if (cid) params.set('clientId', cid)
      setSearchParams(params, { replace: true })
    },
    [depositType, matterId, clientId, setSearchParams]
  )

  const resetForm = () => {
    setSelectedMatter(null)
    setSelectedClient(null)
    const params = new URLSearchParams(searchParams)
    params.delete('matterId')
    params.delete('clientId')
    setSearchParams(params, { replace: true })
    reset({
      ...getFormDefaultValues(),
      destinationAccount: destinationAccountValue ?? undefined
    })
    setOfficeResetKey(new Date().getTime())
    setUseCredits(false)
  }

  const partialReset = () => {
    setSelectedMatter(null)
    setSelectedClient(null)
    const params = new URLSearchParams(searchParams)
    params.delete('matterId')
    params.delete('clientId')
    setSearchParams(params, { replace: true })
    setValue('client', undefined)
    setValue('amount', '')
    setValue('description', '')
    setUseCredits(false)
  }

  const handleDepositTypeChange = (newType: DepositType) => {
    setDepositType(newType)
    updateQueryParams(newType)
  }

  const handleMatterChange = (matter: Maybe<Case>) => {
    setSelectedMatter(matter)
    if (matter?.id) {
      updateQueryParams(undefined, matter.id)
    }
  }

  const handleClientChange = (clientOption: Maybe<ClientOption>) => {
    setSelectedClient(clientOption)
    if (clientOption) {
      setValue('client', { value: clientOption.id, label: clientOption.label })
      updateQueryParams(undefined, undefined, clientOption.id)
    } else {
      setValue('client', undefined)
    }
  }

  const handleRecordClick = () => {
    isAddNewRef.current = false
    setIsAddNew(false)
    handleSubmit(onSubmit)()
  }

  const handleCreateAddNewClick = () => {
    isAddNewRef.current = true
    setIsAddNew(true)
    handleSubmit(onSubmit)()
  }

  // Form submission - show confirmation instead of direct submit
  const onSubmit: SubmitHandler<RecordPaymentFormValues> = async data => {
    if (!clientId && !isFirmPayment) {
      Alert.error('Please select a client.')
      return
    }

    if (isClientPayment && useCredits) {
      const amount = Number(data.amount)
      let maxAmountLimit = clientPayableAmount || 0

      if (amount > clientCreditBalance) {
        Alert.error(`Amount cannot exceed credit balance (${formatCurrency(clientCreditBalance)}).`)
        return
      }

      if (Number(amount) > maxAmountLimit) {
        Alert.error(`Amount must be ${formatCurrency(maxAmountLimit)} or less.`)
        return
      }
    }

    // if (isClientPayment) {
    //   let amount = data.amount
    //   let advancePaymentAmount = 0
    //   let maxAmountLimit = clientPayableAmount || 0

    //   // if (isFlatFee && data.isAdvancePayment && amountToBeAdjusted) {
    //   //   maxAmountLimit = remainingFlatFee
    //   //   amount = clientPayableAmount || 0
    //   //   advancePaymentAmount = amountToBeAdjusted
    //   // }

    //   if (Number(amount) + advancePaymentAmount > maxAmountLimit) {
    //     Alert.error(`Amount must be ${formatCurrency(maxAmountLimit)} or less.`)
    //     return
    //   }
    // }

    setPendingFormData(data)
    //  setShowConfirmation(true)

    if (isFirmPayment) {
      await handleFirmConfirmedSubmit(data)
    } else if (isRefundPayment) {
      await handleRefundConfirmedSubmit(data)
    } else if (isClientCredit) {
      await handleClientCreditConfirmedSubmit(data)
    } else {
      await handleConfirmedSubmit(data)
    }
  }

  // Actual payment execution after confirmation
  const handleConfirmedSubmit = async (data: RecordPaymentFormValues) => {
    if (!clientId) return

    const {
      amount: amountInput,
      description,
      date,
      paymentMethod,
      referenceNo,
      slipNumber,
      destinationAccount,
      // pauseBillingUntil,
      // isAdvancePayment: isAdvance,
      location
    } = data

    const dateTime = toLocalDateTime(date)

    // let amount = amountInput
    // let advancePaymentAmount = 0

    // if (isClientPayment && isFlatFee && isAdvance && amountToBeAdjusted) {
    //   amount = clientPayableAmount || 0
    //   advancePaymentAmount = amountToBeAdjusted
    // }

    const amount = Number(amountInput) || 0
    // Only a CLIENT payment can carry an advance: `amountToBeAdjusted` is the overpayment above the
    // bill's due amount, and `clientPayableAmount` is undefined for every other deposit type, which
    // made the whole deposit look like an advance. The API ignores it for fund requests except when
    // stamping the timeline, so a trust deposit logged "Payment received" at twice its real value.
    const advancePaymentAmount = isClientPayment && amountToBeAdjusted > 0 ? amountToBeAdjusted : 0

    if (useCredits) {
      if (!bill) return

      await applyCreditsMutation({
        variables: {
          applyCreditsInput: {
            clientId,
            billId: bill.id,
            // amount: Number(amount) || 0,
            amount: amount,
            date: dateTime,
            description: description || undefined
          }
        }
      })
      return
    }

    await recordPaymentMutation({
      variables: {
        recordManualPaymentInput: {
          billId: isClientPayment ? bill?.id || 0 : undefined,
          caseId: !isClientPayment ? matterId : undefined,
          clientId,
          // amount: amount || 0,
          // ...(advancePaymentAmount ? { advancePaymentAmount, pauseBillingUntil } : undefined),
          amount: isClientPayment && advancePaymentAmount > 0 ? clientPayableAmount || 0 : amount,
          ...(advancePaymentAmount > 0 ? { advancePaymentAmount } : {}),
          date: dateTime,
          paymentMethod,
          referenceNo,
          slipNumber: slipNumber || null,
          description,
          destinationAccountId: destinationAccount.value,
          locationId: location?.value,
          generateClientBillCopy: isClientPayment ? generateClientBillCopy : undefined
        }
      }
    })
  }
  const handleClientCreditConfirmedSubmit = async (data: RecordPaymentFormValues) => {
    if (!clientId || !matterId) return

    const { amount, description, date, paymentMethod, referenceNo, slipNumber, destinationAccount, location } = data

    const dateTime = toLocalDateTime(date)

    await recordClientCreditsMutation({
      variables: {
        recordClientCreditsInput: {
          caseId: matterId,
          clientId,
          amount: amount || 0,
          date: dateTime,
          paymentMethod,
          referenceNo,
          slipNumber,
          description,
          accountId: destinationAccount.value,
          officeId: location?.value
        }
      }
    })
  }

  const handleRefundConfirmedSubmit = async (data: RecordPaymentFormValues) => {
    if (!clientId || !matterId) return

    const { amount, description, date, paymentMethod, referenceNo, slipNumber, destinationAccount, location } = data

    const dateTime = toLocalDateTime(date)

    await refundPaymentMutation({
      variables: {
        refundPaymentInput: {
          caseId: matterId,
          clientId,
          refundAmount: Number(amount) || 0,
          date: dateTime,
          paymentMethod,
          referenceNo,
          slipNumber: slipNumber || undefined,
          description: description || undefined,
          destinationAccountId: destinationAccount.value,
          locationId: location?.value
        }
      }
    })
  }

  const handleFirmConfirmedSubmit = async (data: RecordPaymentFormValues) => {
    const {
      description,
      date,
      paymentMethod,
      referenceNo,
      slipNumber,
      destinationAccount,
      location,
      vendor,
      glAccount,
      amount
    } = data

    const dateTime = toLocalDateTime(date)

    if (!destinationAccount?.value || !location?.value || !glAccount?.value) {
      Alert.error('Missing required firm payment fields.')
      return
    }

    await createFirmPaymentMutation({
      variables: {
        input: {
          amount: Number(amount) || 0,
          caseId: matterId,
          description,
          officeId: location.value,
          paymentMethod,
          slipNumber,
          date: dateTime,
          bankAccountId: destinationAccount.value,
          referenceNumber: referenceNo,
          vendorId: vendor?.value,
          glAccountId: glAccount.value
        }
      }
    })
  }

  // Derive matter info for confirmation modal
  const matterTitle = bill?.case?.title || selectedMatter?.title || ''
  const matterNumber = selectedMatter?.caseNumber || bill?.case?.consultMatterNumber || ''

  const showBillPreview = isClientPayment && matterId && bill
  const showNoBillMessage = isClientPayment && matterId && !bill && !billLoading

  const disableRecordTransaction = isFirmPayment
    ? createFirmPaymentLoading
    : isRefundPayment
    ? refundPaymentLoading || !matterId || !clientId
    : recordPaymentLoading ||
      recordClientCreditsLoading ||
      applyCreditsLoading ||
      !matterId ||
      (isClientPayment && !bill) ||
      (isClientPayment && isPreBill)
  const showRecordTransactionLoading =
    recordPaymentLoading ||
    recordClientCreditsLoading ||
    createFirmPaymentLoading ||
    applyCreditsLoading ||
    refundPaymentLoading

  return (
    <FormProvider {...methods}>
      <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={3} width="100%" alignItems="stretch">
        {/* Left pane: Form */}
        <Box
          flex={showBillPreview ? { xs: '1 1 100%', lg: '0 0 60%' } : '1 1 100%'}
          minWidth={0}
          sx={{ overflow: 'auto' }}
        >
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3} md={12} lg={12} xl={isClientPayment ? 12 : 8}>
              {/* Deposit Type Radio Group */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                  Deposit Type
                </Typography>
                <RadioGroup
                  row
                  value={depositType}
                  onChange={e => handleDepositTypeChange(e.target.value as DepositType)}
                >
                  {Object.entries(DEPOSIT_TYPE_LABELS).map(([value, label]) => (
                    <FormControlLabel key={value} value={value} control={<Radio />} label={label} />
                  ))}
                </RadioGroup>
              </Grid>

              {/* Matter and Client Dropdowns */}
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={isFirmPayment ? 12 : 6}>
                    <DepositMatterDropdown
                      matterId={searchParams.get('matterId') || undefined}
                      onChange={handleMatterChange}
                      required={!isFirmPayment}
                      autoSelectOnTab
                    />
                    <Box display="flex" gap={2} mt={0.75}>
                      <MuiLink
                        href={selectedMatter ? `/matters/matter-detail/${selectedMatter.id}` : undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{ pointerEvents: selectedMatter ? 'auto' : 'none', opacity: selectedMatter ? 1 : 0.4 }}
                      >
                        Matter Detail
                      </MuiLink>
                      <MuiLink
                        href={selectedMatter ? `/matters/matter-detail/${selectedMatter.id}?active-tab=2` : undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{ pointerEvents: selectedMatter ? 'auto' : 'none', opacity: selectedMatter ? 1 : 0.4 }}
                      >
                        Financials
                      </MuiLink>
                      <Tooltip title={selectedMatter && !isClientPayment ? 'Switch to Client Payment' : ''} arrow>
                        <span style={{ display: 'inline-flex' }}>
                          <MuiLink
                            component="button"
                            type="button"
                            variant="caption"
                            sx={{
                              cursor: selectedMatter && isClientPayment ? 'pointer' : 'default',
                              pointerEvents: selectedMatter && isClientPayment ? 'auto' : 'none',
                              opacity: selectedMatter && isClientPayment ? 1 : 0.4
                            }}
                            onClick={() => setShowQuickBillModal(true)}
                          >
                            Quick Bill
                          </MuiLink>
                        </span>
                      </Tooltip>
                      {selectedMatter?.billing?.billingType === BillingType.FlatFee && (
                        <Tooltip title={!isClientPayment ? 'Switch to Client Payment' : ''} arrow>
                          <span style={{ display: 'inline-flex' }}>
                            <MuiLink
                              component="button"
                              type="button"
                              variant="caption"
                              sx={{
                                cursor: isClientPayment ? 'pointer' : 'default',
                                pointerEvents: isClientPayment ? 'auto' : 'none',
                                opacity: isClientPayment ? 1 : 0.4
                              }}
                              onClick={() => setShowMonthlyFlatFeeModal(true)}
                            >
                              Monthly Flat Fee Bill
                            </MuiLink>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                  {!isFirmPayment && (
                    <Grid item xs={12} md={6}>
                      <DepositClientDropdown
                        caseId={matterId}
                        clientId={searchParams.get('clientId') || undefined}
                        autoSelectOnTab
                        onChange={handleClientChange}
                      />
                      <Box mt={0.75}>
                        <MuiLink
                          href={selectedClient ? `/contacts/client/${selectedClient.clientNumber}` : undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{ pointerEvents: selectedClient ? 'auto' : 'none', opacity: selectedClient ? 1 : 0.4 }}
                        >
                          Client Details
                        </MuiLink>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} mt={2}>
                <Grid container spacing={3}>
                  {isClientPayment && clientCreditBalance > 0 && bill && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={useCredits}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setUseCredits(e.target.checked)
                              setValue('amount', Math.min(clientCreditBalance, Number(clientPayableAmount)))
                            }}
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Typography>
                            Apply credits to this bill ({formatCurrency(clientCreditBalance)} available)
                          </Typography>
                        }
                      />
                    </Grid>
                  )}

                  {isFirmPayment && (
                    <>
                      <Grid item xs={12} md={6}>
                        <VendorAutoComplete
                          name="vendor"
                          label="Vendor"
                          errorMessage={errors.vendor?.message}
                          setGlAccount={true}
                          sx={{ my: 0 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            name="glAccount"
                            control={control}
                            render={({ field }) => (
                              <AccountDropdown
                                key={field.name}
                                value={field.value ?? null}
                                required={true}
                                onChange={field.onChange}
                                searchFilters={{ isBankAccount: false }}
                                errorMessage={errors.glAccount?.value?.message || errors.glAccount?.message}
                                label="GL Account"
                                sx={{ my: 0 }}
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </>
                  )}

                  {/* Advance Payment checkbox (flat fee client payments only) */}
                  {/* {isClientPayment && isFlatFee && !useCredits && (
                    <Grid item xs={12}>
                      <CheckboxController name="isAdvancePayment" label="Advance Payment" size="small" />
                    </Grid>
                  )} */}

                  <Grid
                    container
                    item
                    xs={12}
                    // lg={isAdvancePayment && isClientPayment ? 8 : 12}
                    lg={12}
                    spacing={3}
                  >
                    {!useCredits && (
                      <Grid item xs={12} md={5}>
                        <FormControl variant="outlined" fullWidth>
                          <Controller
                            name="destinationAccount"
                            control={control}
                            render={({ field }) => (
                              <AccountDropdown
                                key={`${field.name}-${depositType}`}
                                value={field.value}
                                onChange={field.onChange}
                                searchFilters={destinationAccountFilters}
                                errorMessage={
                                  errors.destinationAccount?.message || errors.destinationAccount?.value?.message
                                }
                                label={'Destination Account'}
                              />
                            )}
                          />
                        </FormControl>
                        <Box mt={0.75}>
                          <MuiLink
                            href={
                              destinationAccountValue?.value
                                ? `${PAYMENTS_ROUTE}?${QUERY_PARAMS.ACCOUNT_ID}=${destinationAccountValue.value}`
                                : undefined
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            sx={{
                              pointerEvents: destinationAccountValue?.value ? 'auto' : 'none',
                              opacity: destinationAccountValue?.value ? 1 : 0.4
                            }}
                          >
                            Recent Payments
                          </MuiLink>
                        </Box>
                        <Box mt={1.5}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isClientPayment && generateClientBillCopy}
                                disabled={!isClientPayment}
                                onChange={e => setGenerateClientBillCopy(e.target.checked)}
                              />
                            }
                            label="Generate new client bill copy"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: -0.5, ml: 3.75, maxWidth: 520 }}
                          >
                            Check this only if you are applying a trust payment to this bill and want to generate an
                            updated copy of the bill for the client. The new copy will be available on the bill detail
                            page.
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={12} md={useCredits ? 6 : 3}>
                      <CustomController
                        dollarAdornment
                        controllerName="amount"
                        controllerLabel="Amount"
                        fieldType="number"
                        sx={{ my: 0 }}
                      />
                      {isClientPayment && (clientPayableAmount ?? 0) > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1 }}>
                          Due Amt: {formatCurrency(clientPayableAmount || 0)}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} md={useCredits ? 6 : 4}>
                      <CustomDatePickerController
                        controllerName="date"
                        controllerLabel="Date"
                        disablePast={false}
                        sx={{ my: 0 }}
                      />
                    </Grid>

                    {hasExtraPayment && (
                      <Grid item xs={12}>
                        <MuiAlert severity="error" sx={{ alignItems: 'center' }}>
                          <Typography variant="body2" component="div">
                            <strong>Extra Payment:</strong> The bill amount is{' '}
                            <strong>{formatCurrency(clientPayableAmount || 0)}</strong> but you are paying{' '}
                            <strong>{formatCurrency(paymentAmount)}</strong>.
                          </Typography>
                          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                            The extra amount of <strong>{formatCurrency(amountToBeAdjusted)}</strong> will be added to
                            the client&apos;s credit balance and can be used for future payments.
                          </Typography>
                        </MuiAlert>
                      </Grid>
                    )}

                    {!useCredits && (
                      <>
                        <Grid item xs={12} md={6}>
                          <PaymentMethodController
                            name="paymentMethod"
                            label="Payment By"
                            paymentMethods={availablePaymentMethods}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <CustomController
                            controllerName="referenceNo"
                            controllerLabel="Reference No."
                            fieldType="text"
                            sx={{ my: 0 }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <OfficeAutoComplete
                            name="location"
                            label="Location of Payment"
                            required
                            resetKey={officeResetKey}
                            autoSelectOnTab
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <CustomController controllerName="slipNumber" controllerLabel="Slip No." fieldType="text" />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <CustomController
                        controllerName="description"
                        controllerLabel="Notes"
                        fieldType="textarea"
                        isMultiLine
                        rowsLength={4}
                        sx={{ my: 0 }}
                      />
                    </Grid>
                  </Grid>

                  {/* Advance payment details (client flat fee only) */}
                  {/* {isAdvancePayment && isClientPayment && (
                    <Grid container item xs={12} lg={4} alignContent={'flex-start'} spacing={3}>
                      <Grid item xs={12}>
                        <Typography fontSize={14}>
                          Bill&apos;s due amount is <b>{formatCurrency(clientPayableAmount || 0)}</b> but client is
                          paying <b>{formatCurrency(paymentAmount)}</b> so remaining{' '}
                          <b>{formatCurrency(amountToBeAdjusted)}</b> will be adjusted towards his remaining contract
                          balance.
                        </Typography>

                        <Typography fontSize={14} mt={1.8} mb={0.5}>
                          <b>Amount to be adjusted:</b> {formatCurrency(amountToBeAdjusted)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <CustomDatePickerController
                          controllerName="pauseBillingUntil"
                          controllerLabel="Billing Paused Until"
                          sx={{ my: 0 }}
                        />
                        <InfoTooltip info="Billing paused means that the monthly retainer amount and special payments (if any) will not be charged until the date selected in this field. However, all the expense entries, cost entries or other legal fee entries will still be part of the monthly bill." />
                      </Grid>

                      <Grid item xs={12}>
                        <TableContainer>
                          <CustomTable>
                            <TableBody>
                              <TableRow>
                                <TableCell>Current Contract Balance before Adjustment: </TableCell>
                                <TableCell>{formatCurrency(remainingFlatFee)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Remaining Contract Balance after Adjustment</TableCell>
                                <TableCell>{formatCurrency(contractAfterAmount)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </CustomTable>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  )} */}

                  {/* No bill message for client payments */}
                  {showNoBillMessage && (
                    <Grid item xs={12}>
                      <MuiAlert severity="warning">
                        No active bill found for this matter.{' '}
                        <MuiLink
                          component="button"
                          type="button"
                          variant="body2"
                          sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}
                          onClick={() => setShowQuickBillModal(true)}
                        >
                          Click here to create a new bill
                        </MuiLink>
                      </MuiAlert>
                    </Grid>
                  )}

                  {/* Submit button */}
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" gap={2} pt={2}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="inherit"
                        disabled={!isDirty && !selectedMatter && !selectedClient}
                        onClick={() => setShowResetConfirmation(true)}
                      >
                        Reset
                      </Button>
                      <Button
                        type="button"
                        color="primary"
                        variant="outlined"
                        disabled={disableRecordTransaction}
                        endIcon={showRecordTransactionLoading && <CircularProgress size={20} color="inherit" />}
                        onClick={handleCreateAddNewClick}
                      >
                        Create & Add New
                      </Button>
                      <Button
                        type="button"
                        color="primary"
                        variant="contained"
                        disabled={disableRecordTransaction}
                        endIcon={showRecordTransactionLoading && <CircularProgress size={20} color="inherit" />}
                        onClick={handleRecordClick}
                      >
                        Record Transaction
                      </Button>
                    </Box>
                  </Grid>

                  {/* Success info box after recording */}
                  {!isFirmPayment && lastRecordedTransaction && (
                    <Grid item xs={12}>
                      <MuiAlert
                        severity="success"
                        action={
                          <IconButton size="small" aria-label="close" onClick={() => setLastRecordedTransaction(null)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <Typography variant="body2" component="div" gutterBottom fontWeight={600} color="text.primary">
                          Transaction recorded successfully
                        </Typography>
                        <Typography variant="body1" component="div" color="text.secondary">
                          <strong>Matter:</strong> {lastRecordedTransaction.matterTitle}
                          {lastRecordedTransaction.matterNumber
                            ? ` (#${lastRecordedTransaction.matterNumber})`
                            : ''}. <strong>Matter ID:</strong>{' '}
                          {lastRecordedTransaction.matterNumber || lastRecordedTransaction.matterId}.{' '}
                          <strong>Client:</strong> {lastRecordedTransaction.clientName}
                          {lastRecordedTransaction.clientNumber
                            ? ` (#${lastRecordedTransaction.clientNumber})`
                            : ''}. <strong>Deposit type:</strong>{' '}
                          {DEPOSIT_TYPE_LABELS[lastRecordedTransaction.depositType]}.{' '}
                          <strong>Destination account:</strong> {lastRecordedTransaction.destinationAccountLabel}.{' '}
                          <strong>Payment method:</strong>{' '}
                          {(lastRecordedTransaction.paymentMethod || '').replace(/CHEQUE/g, 'CHECK')}.{' '}
                          <strong>Amount:</strong> {formatCurrency(lastRecordedTransaction.amount)}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {'You can view this recorded transaction here. - '}
                          <Link
                            to={`/matters/matter-detail/${lastRecordedTransaction.matterId}?active-tab=2`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: 'primary.main',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              fontSize: '0.875rem'
                            }}
                          >
                            View matter details
                          </Link>
                          {lastRecordedTransaction.depositType === DepositType.ESCROW && (
                            <>
                              {' - '}
                              <Link
                                to={`${PAYMENTS_ROUTE}?${QUERY_PARAMS.MATTER}=${lastRecordedTransaction.matterId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: 'primary.main',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  fontSize: '0.875rem'
                                }}
                              >
                                View Payments
                              </Link>
                            </>
                          )}
                        </Typography>
                      </MuiAlert>
                    </Grid>
                  )}

                  {isFirmPayment && lastRecordedTransaction && (
                    <Grid item xs={12}>
                      <FirmPaymentSuccessMessage
                        info={lastRecordedTransaction}
                        onClose={() => setLastRecordedTransaction(null)}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Box>

        {/* Right pane: Bill Preview (client payment only) */}
        {showBillPreview && (
          <Box flex={{ xs: '1 1 100%', lg: '0 0 40%' }} minWidth={0} sx={{ overflow: 'auto', pr: 2 }}>
            {isPreBill && (
              <MuiAlert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  canAdminApprovePreBill ? (
                    <Button
                      color="primary"
                      size="small"
                      variant="contained"
                      onClick={() => setShowAdminApprovalConfirmation(true)}
                      disabled={approvingByAttorney || approvingByDepartmentHead}
                    >
                      Approve
                    </Button>
                  ) : undefined
                }
              >
                This is a pre-bill. You need to approve it in order to record a client payment.
              </MuiAlert>
            )}
            <BillPreviewPanel
              bill={bill}
              clientData={billClientData}
              interestData={interestData}
              outstandingBillsData={outstandingBillsData}
              selectedClientId={clientId}
            />
          </Box>
        )}
      </Box>

      {/* Confirmation Modal */}
      {/* {!isFirmPayment && (
        <DepositConfirmationModal
          isOpen={showConfirmation}
          onClose={() => {
            setShowConfirmation(false)
            setPendingFormData(null)
          }}
          onConfirm={
            isRefundPayment
              ? handleRefundConfirmedSubmit
              : isClientCredit
              ? handleClientCreditConfirmedSubmit
              : handleConfirmedSubmit
          }
          loading={recordPaymentLoading || recordClientCreditsLoading || applyCreditsLoading || refundPaymentLoading}
          depositType={depositType}
          matterTitle={matterTitle}
          matterNumber={matterNumber}
          matterId={matterId || ''}
          clientName={selectedClient?.label || ''}
          clientNumber={selectedClient?.clientNumber || ''}
          destinationAccountLabel={destinationAccountValue?.label || ''}
          paymentMethod={useCredits ? 'Apply Credits' : pendingFormData?.paymentMethod || ''}
          amount={Number(pendingFormData?.amount) || 0}
          useCreditsMode={useCredits}
          addNewMode={isAddNew}
          extraAmount={amountToBeAdjusted}
        />
      )}

      {isFirmPayment && showConfirmation && (
        <FirmPaymentConfirmationModal
          isOpen={showConfirmation}
          onClose={() => {
            setShowConfirmation(false)
            setPendingFormData(null)
          }}
          onConfirm={handleFirmConfirmedSubmit}
          loading={createFirmPaymentLoading}
          vendorLabel={pendingFormData?.vendor?.label || ''}
          glAccountLabel={pendingFormData?.glAccount?.label || ''}
          paymentMethod={pendingFormData?.paymentMethod || ''}
          amount={Number(pendingFormData?.amount)}
          destinationAccountLabel={destinationAccountValue?.label || ''}
          addNewMode={isAddNew}
        />
      )} */}

      <ConfirmationModal
        isOpen={showResetConfirmation}
        title="Reset Form"
        message="Are you sure you want to reset the form? All entered data will be cleared."
        confirmText="Reset"
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => {
          resetForm()
          setShowResetConfirmation(false)
        }}
        onCancel={() => setShowResetConfirmation(false)}
      />

      {showAdminApprovalConfirmation && bill && (
        <ConfirmationModal
          isOpen={showAdminApprovalConfirmation}
          title="Approve Bill"
          message={getAdminApprovalConfirmationDialogDescription()}
          confirmText="Approve"
          cancelText="Cancel"
          variant="danger"
          isLoading={approvingByAttorney || approvingByDepartmentHead}
          onConfirm={() => handleAdminApproval()}
          onCancel={() => setShowAdminApprovalConfirmation(false)}
        />
      )}

      {showMonthlyFlatFeeModal && selectedMatter && (
        <GenerateMonthlyFlatFeeBillModal
          isOpen={showMonthlyFlatFeeModal}
          onClose={() => setShowMonthlyFlatFeeModal(false)}
          caseId={selectedMatter.id}
          caseUsers={selectedMatter.caseToUsers || []}
          billingType={selectedMatter.billing?.billingType || null}
          refetch={refetchBill}
          disableNavigateOnSuccess
        />
      )}

      {showQuickBillModal && selectedMatter && (
        <QuickBillModal
          isOpen={showQuickBillModal}
          onClose={() => setShowQuickBillModal(false)}
          caseId={selectedMatter.id}
          refetch={refetchBill}
        />
      )}
    </FormProvider>
  )
}

export default DepositFundForm
